import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import * as api from "../../lib/api.js";
import { t } from "../contract/strings";
import { ScreenHeading, SkeletonGate, StatusLine } from "../ui";
import {
  isWritingUp,
  openPastSession,
  PastSessionCard,
  PastSessionCardSkeleton,
  type PastSession,
} from "./past-sessions";
import { Screen } from "./screen";
import { useScreenStatus } from "./use-screen-status";

/**
 * Cards per page. Eight is one screenful of the pane, and with the server's
 * `X-Total-Count` it is also how "is there more" is answered -- no extra row and no page
 * numbers.
 */
export const PAGE_SIZE = 8;

/** How often the processing rows on screen re-ask for their stage. Stages last minutes. */
const STAGE_POLL_MS = 10_000;

/**
 * The session archive: every session this account's devices recorded or joined, newest
 * first, loaded as the list is scrolled.
 *
 * There is no pager and no page state. The list asks for the next page when its end marker
 * comes within 96px of the pane's bottom -- the transcript's own rule -- so the offset is
 * simply how many rows are already held. The pane is the only thing that scrolls: the app
 * header and the screen's heading are fixed (`body[data-screen="sessions"]` in `app.css`).
 *
 * The guard is `signed-in`: history belongs to the account, and a signed-out device has
 * none to show.
 */
export function Sessions() {
  const navigate = useNavigate();
  const [status, say] = useScreenStatus();

  const [rows, setRows] = useState<PastSession[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const paneRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  /** One fetch at a time: an intersection that arrives mid-flight is dropped, not queued. */
  const inFlight = useRef(false);

  const hasMore = rows !== null && total !== null && rows.length < total;
  const empty = rows !== null && rows.length === 0;
  const allLoaded = rows !== null && rows.length > 0 && total !== null && rows.length >= total;

  useEffect(() => {
    let alive = true;
    api.accountSessions({ limit: PAGE_SIZE, offset: 0 }).then(
      (fetched: PastSession[] & { total?: number | null }) => {
        if (!alive) return;
        setTotal(typeof fetched.total === "number" ? fetched.total : null);
        setRows(fetched.slice(0, PAGE_SIZE));
        setLoading(false);
      },
      (err: Error) => {
        if (!alive) return;
        setRows(null);
        setTotal(null);
        setLoading(false);
        console.warn("listSessions failed", err);
        say(t("sessions.error"), "err");
      }
    );
    return () => {
      alive = false;
    };
  }, [say]);

  /** One page, appended. A failure keeps the rows already on screen. */
  const loadMore = useCallback(() => {
    const loaded = rows?.length ?? 0;
    if (inFlight.current || total === null || loaded >= total) return;
    inFlight.current = true;
    setLoadingMore(true);
    api.accountSessions({ limit: PAGE_SIZE, offset: loaded }).then(
      (fetched: PastSession[] & { total?: number | null }) => {
        if (typeof fetched.total === "number") setTotal(fetched.total);
        setRows((current) => {
          const held = new Set((current ?? []).map((row) => row.session_id));
          return [...(current ?? []), ...fetched.filter((row) => !held.has(row.session_id))];
        });
        setLoadingMore(false);
        inFlight.current = false;
      },
      (err: Error) => {
        // The rows above are still good, so this is a line at the screen's foot, not a
        // page-wide failure. Scrolling again retries.
        setLoadingMore(false);
        inFlight.current = false;
        console.warn("listSessions more failed", err);
        say(t("sessions.error"), "err");
      }
    );
  }, [rows, total, say]);

  useEffect(() => {
    if (!hasMore || typeof IntersectionObserver === "undefined") return;
    const pane = paneRef.current;
    const sentinel = sentinelRef.current;
    if (!pane || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      // Rooted on the pane, not the viewport: this list is the only thing that scrolls.
      { root: pane, rootMargin: "0px 0px 96px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  /*
   * Refresh what is held while any readable row is being written up. The list carries the
   * stages, so no transcript downloads are needed just to update a mark; re-reading the
   * held range (offset 0, limit = held) replaces it without disturbing the scroll.
   */
  useEffect(() => {
    const held = rows ?? [];
    if (!held.some((row) => isWritingUp(row))) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = async () => {
      try {
        const fetched = await api.accountSessions({ limit: held.length, offset: 0 });
        if (!alive) return;
        if (typeof fetched.total === "number") setTotal(fetched.total);
        setRows((current) => (current ? fetched.slice(0, current.length) : current));
      } catch {
        if (alive) timer = setTimeout(ask, STAGE_POLL_MS);
      }
    };
    timer = setTimeout(ask, STAGE_POLL_MS);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [rows]);

  return (
    <Screen name="sessions">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-none">
          <ScreenHeading>{t("sessions.heading")}</ScreenHeading>
        </div>

        {/* The pane: the only thing that scrolls. It takes focus so a keyboard can scroll
            it, and it is named by the heading above it. `relative` makes it the containing
            block of the `sr-only` status below, which is absolutely positioned: without it
            that line escapes the pane's clip and scrolls the page. */}
        <div
          ref={paneRef}
          role="region"
          aria-label={t("sessions.heading")}
          tabIndex={0}
          className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4"
        >
          <SkeletonGate
            loading={rows === null && loading}
            label={t("sessions.loading")}
            skeleton={
              <div className="grid gap-4">
                {Array.from({ length: PAGE_SIZE }, (_, i) => (
                  <PastSessionCardSkeleton key={i} />
                ))}
              </div>
            }
          >
            {empty ? (
              <div className="bg-surface border border-line rounded-card p-pad my-4 text-center">
                <p className="m-0 text-lede">{t("sessions.empty")}</p>
                <p className="m-0 mt-2 text-note text-ink-soft">{t("sessions.empty_hint")}</p>
              </div>
            ) : null}

            <div className="grid gap-4 my-4">
              {(rows ?? []).map((session) => (
                <PastSessionCard
                  key={session.session_id}
                  session={session}
                  onOpen={(s) => openPastSession(s, navigate)}
                />
              ))}
            </div>

            {loadingMore ? (
              <div className="mb-4">
                <PastSessionCardSkeleton />
              </div>
            ) : null}

            {/* The end marker, and the observer's only target. It exists while there is
                more, so the observer disconnects itself when the archive runs out. */}
            {hasMore ? <div ref={sentinelRef} className="h-px" aria-hidden="true" /> : null}

            {allLoaded && !loadingMore ? (
              <p className="m-0 mt-4 font-code text-count uppercase tracking-label text-ink-soft text-center">
                {t("sessions.all_loaded")}
              </p>
            ) : null}

            {/* One announcement for the wait and for the end, not one per scroll. */}
            <p className="sr-only" role="status">
              {loadingMore ? t("sessions.loading_more") : allLoaded ? t("sessions.all_loaded") : ""}
            </p>
          </SkeletonGate>
        </div>

        <StatusLine kind={status.kind}>{status.message}</StatusLine>
      </div>
    </Screen>
  );
}
